import { useTranslation } from 'react-i18next'

import { type BadgeProps } from '@enechain/ecloud-designsystem'
import { FaTriangleExclamationSolid } from '@enechain/ecloud-icons'

import { Badge } from '~/components/common/Badge'
import { iconSize } from '~/constants/iconSize'

/**
 * enum 値 → 状態バッジ表示仕様。
 *
 * 色は DS `Badge` の semantic status (`info` / `warning` / `positive` / `negative` /
 * `neutral`) だけで表す。 生カラー名 (`colorPalette`) や `variant` を渡す口は意図的に
 * 持たない (= 状態を生カラーで表す経路を型で塞ぐ)。
 */
export type EnumBadgeSpec = {
  status: BadgeProps['status']
  /** 表示ラベルの i18n key。 */
  labelKey: string
  /**
   * ラベルの前に警告アイコン (filled warning triangle) を置くか。 色だけに頼らず
   * 「問題がある状態」 を形でも示す時に使う (例: CSV 差分プレビューのエラー行。
   * design `.badge-error` 内の warning icon)。 アイコンの色は Badge の文字色に従う。
   */
  warningIcon?: boolean
}

type Props<E extends number> = {
  value: E
  /**
   * enum 値 → 表示仕様の**網羅** map (= UNSPECIFIED 含む全メンバ必須)。
   * proto enum に値が増えると `Record` の網羅性チェックで型エラーになり、 追従漏れを検出できる。
   */
  map: Record<E, EnumBadgeSpec>
}

/**
 * proto enum を DS `Badge` の semantic status で表示する共通コンポーネント。
 *
 * `enum 値 → status / ラベル` の対応を呼び出し側が `Record` で宣言し、 本体は
 * 「map 参照 → t() → Badge」 だけを担う。
 *
 * 想定外の値 (= map に無い数値) が来た場合は UNSPECIFIED (= 0) の仕様に
 * fallback する (= proto enum は必ず 0 を持つため、 wire に新しい値が先行しても
 * 表示が壊れない)。
 */
export const EnumStatusBadge = <E extends number>({
  value,
  map,
}: Props<E>): React.ReactElement => {
  const { t } = useTranslation()
  const spec = map[value] ?? map[0 as E]
  if (spec.warningIcon !== true) {
    return <Badge status={spec.status}>{t(spec.labelKey)}</Badge>
  }
  return (
    <Badge alignItems="center" gap={1} status={spec.status}>
      <FaTriangleExclamationSolid
        boxSize={iconSize.badgeIcon}
        aria-hidden="true"
      />
      {t(spec.labelKey)}
    </Badge>
  )
}
